import { FormatLineSpacingSharp } from "@material-ui/icons";
import { createSlice } from "@reduxjs/toolkit";
import {
  ISOLINE_FULL_UNITS,
  ISOLINE_MARKS,
  ISOLINE_MODES,
  ISOLINE_UNITS,
  MEASUREMENT_BY_MODE,
  METERS_TO_MILES,
} from "data/models/IsolinesModel";

const slice = createSlice({
  name: "app",
  initialState: {
    error: null,
    bottomSheetOpen: false,
    isoline: {
      loading: false,
      data: null,
      mode: ISOLINE_MODES.RADIUS,
      range: null,
    },
    openAnalysisForm: null,
    isDragging: false,
    isDropAPin: false,
    simulations: {
      features: [],
      visible: false,
    },
    visitorsOrigin: null,
    day: "All",
    window: "All",
    n_visitors: null,
    data: null,
    breaks: [1, 2, 3],
    dataOrigins: null,
    dataOriginsIntermediateZoomOut: null,
    dataOriginsZoomOut: null,
    breaksOrigins: [1, 2, 3, 4, 5, 6],
    travelledCountries: null,
    visitedBrands: null,
    geobehaviour: null,
  },
  reducers: {
    setError: (state, action) => {
      state.error = action.payload;
    },
    setBottomSheetOpen: (state, action) => {
      state.bottomSheetOpen = action.payload;
    },
    setOpenAnalysisForm: (state, { payload }) => {
      state.openAnalysisForm = payload;
    },
    setIsolineLoading: (state, { payload }) => {
      state.isoline.loading = payload;
    },
    setSimulations: (state, { payload }) => {
      state.simulations.features = payload;
    },
    setIsoline: (state, { payload }) => {
      state.isoline = {
        ...state.isoline,
        loading: false,
        data: payload,
      };
    },
    setIsolineMode: (state, { payload }) => {
      state.isoline.mode = payload;
    },
    setIsolineRange: (state, { payload }) => {
      state.isoline.range = payload;
    },
    setIsDragging: (state, { payload }) => {
      state.isDragging = payload;
    },
    setSelectedFeature: (state, { payload }) => {
      state.selectedFeature = payload
        ? { ...state.selectedFeature, ...payload }
        : null;
    },
    setSelectedLocation: (state, { payload }) => {
      state.selectedLocation = payload;
    },
    setSimulationsVisible: (state, { payload }) => {
      state.simulations.visible = payload;
    },
    setVisitorsOrigin: (state, action) => {
      state.visitorsOrigin = action.payload;
    },
    setTravelledCountries: (state, action) => {
      state.travelledCountries = action.payload;
    },
    setVisitedBrands: (state, action) => {
      state.visitedBrands = action.payload;
    },
    setGeobehaviour: (state, action) => {
      state.geobehaviour = action.payload;
    },
    setDay: (state, action) => {
      state.day = action.payload;
    },
    setWindow: (state, action) => {
      state.window = action.payload;
    },
    setVisitors: (state, action) => {
      state.n_visitors = action.payload;
    },
    setData: (state, action) => {
      state.data = action.payload;
    },
    setBreaks: (state, action) => {
      state.breaks = action.payload;
    },
    setDataOrigins: (state, action) => {
      state.dataOrigins = action.payload;
    },
    setDataOriginsZoomOut: (state, action) => {
      state.dataOriginsZoomOut = action.payload;
    },
    setDataOriginsIntermediateZoomOut: (state, action) => {
      state.dataOriginsIntermediateZoomOut = action.payload;
    },
    setBreaksOrigins: (state, action) => {
      state.breaksOrigins = action.payload;
    },
    setDropAPin: (state, { payload }) => {
      state.isDropAPin = payload;
    },
  },
});

export default slice.reducer;

export const setError = (payload) => ({ type: "app/setError", payload });
export const setBottomSheetOpen = (payload) => ({
  type: "app/setBottomSheetOpen",
  payload,
});
export const setVisitorsOrigin = (payload) => ({
  type: "app/setVisitorsOrigin",
  payload,
});
export const setDay = (payload) => ({ type: "app/setDay", payload });
export const setWindow = (payload) => ({ type: "app/setWindow", payload });
export const setVisitors = (payload) => ({ type: "app/setVisitors", payload });
export const setData = (payload) => ({ type: "app/setData", payload });
export const setBreaks = (payload) => ({ type: "app/setBreaks", payload });
export const setDropAPin = (payload) => ({ type: "app/setDropAPin", payload });
export const setDataOrigins = (payload) => ({
  type: "app/setDataOrigins",
  payload,
});
export const setDataOriginsZoomOut = (payload) => ({
  type: "app/setDataOriginsZoomOut",
  payload,
});
export const setDataOriginsIntermediateZoomOut = (payload) => ({
  type: "app/setDataOriginsIntermediateZoomOut",
  payload,
});
export const setBreaksOrigins = (payload) => ({
  type: "app/setBreaksOrigins",
  payload,
});
export const setTravelledCountries = (payload) => ({
  type: "app/setTravelledCountries",
  payload,
});
export const setVisitedBrands = (payload) => ({
  type: "app/setVisitedBrands",
  payload,
});
export const setGeobehaviour = (payload) => ({
  type: "app/setGeobehaviour",
  payload,
});
export const setOpenAnalysisForm = slice.actions.setOpenAnalysisForm;
export const setIsDragging = slice.actions.setIsDragging;
export const setSelectedFeature = slice.actions.setSelectedFeature;
export const setSelectedLocation = slice.actions.setSelectedLocation;
export const setIsolineLoading = slice.actions.setIsolineLoading;
export const setIsoline = slice.actions.setIsoline;
export const setIsolineMode = slice.actions.setIsolineMode;
export const setIsolineRange = slice.actions.setIsolineRange;
export const setSimulations = slice.actions.setSimulations;
export const setSimulationsVisible = slice.actions.setSimulationsVisible;
export const selectSelectedFeature = (state) => state.app.selectedFeature;
export const selectSelectedLocation = (state) => state.app.selectedLocation;

export const selectSelectedIsolineRange = (state) => {
  const selectedMode = selectIsolineMode(state);
  const isolineMarks = selectIsolineMarks(selectedMode)(state);
  return state.app.isoline.range || isolineMarks[0];
};

export const selectIsolineMarks = (mode) => (state) => {
  const marks = ISOLINE_MARKS[mode];
  return marks;
};

export const selectIsolineUnit = (mode) => (state) => {
  const measurement = MEASUREMENT_BY_MODE[mode];
  return ISOLINE_UNITS[mode];
};

export const selectIsolineFullUnit = (mode) => (state) => {
  const measurement = MEASUREMENT_BY_MODE[mode];
  return ISOLINE_FULL_UNITS[mode];
};

export const selectIsolineMode = (state) => {
  const selectedMode = state.app.isoline.mode;

  return selectedMode;
};
